import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@kss/api';
import { Button, Card, useToast } from '@kss/ui';
import { RefreshCw, ReceiptText, UtensilsCrossed } from 'lucide-react';
import { createHeaderAndGetId, postLinesSequential } from '../../utils/postLines';
import { postStockMovements, resolveDefaultWarehouseId } from '../../utils/inventory';

/**
 * "Open Orders — ready to bill" strip for the Dine-in Bills page
 * (`/panel/restaurant/invoices`). Lists RestaurantOrders that are NOT yet
 * billed and have no SalesInvoice yet, and a one-click **Generate Bill** that
 * creates the Dine-in SalesInvoice + its line rows (reliably, via
 * createHeaderAndGetId + postLinesSequential), posts stock-out, and flips the
 * order to Billed. Lets staff bill an order from the invoices page instead of
 * only from the Floor. (#9)
 */

const round2 = (n: number) => Math.round(n * 100) / 100;

function pickList(d: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
}

interface BillLine { name: string; qty: number; rate: number; taxRate: number; productId: number | string | null }
interface OpenOrder {
  id: number | string;
  code: string;
  number: string;
  customerName: string;
  tableLabel: string;
  lines: BillLine[];
  subTotal: number;
  tax: number;
  grand: number;
}

// Orders in these stages are done (or never billable) — hide from the strip.
const DONE = new Set(['billed', 'paid', 'closed', 'completed', 'cancelled', 'void', 'voided', 'refunded']);

export default function DineInOpenOrders({ onBilled }: { onBilled?: () => void }) {
  const toast = useToast();
  const [orders, setOrders] = useState<Array<Record<string, unknown>>>([]);
  const [orderLines, setOrderLines] = useState<Array<Record<string, unknown>>>([]);
  const [tableMap, setTableMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, lRes, tRes] = await Promise.all([
        apiClient.get('/restaurant/RestaurantOrder').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/RestaurantOrderLine').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/RestaurantTable').catch(() => ({ data: [] })),
      ]);
      setOrders(pickList(oRes.data));
      setOrderLines(pickList(lRes.data));
      const tm = new Map<string, string>();
      for (const t of pickList(tRes.data)) {
        const id = String(t.id ?? t.tableId ?? '');
        if (id) tm.set(id, String(t.name ?? t.code ?? `Table ${id}`));
      }
      setTableMap(tm);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const open = useMemo<OpenOrder[]>(() => {
    const byOrder = new Map<string, BillLine[]>();
    for (const l of orderLines) {
      const oid = String(l.restaurantOrderId ?? '');
      if (!oid || oid === '0') continue;
      const arr = byOrder.get(oid) ?? byOrder.set(oid, []).get(oid)!;
      arr.push({
        name: String(l.itemName ?? l.productName ?? l.name ?? '—'),
        qty: Number(l.quantity ?? 1) || 1,
        rate: Number(l.unitPrice ?? l.rate ?? l.price ?? 0) || 0,
        taxRate: Number(l.taxRate ?? 0) || 0,
        productId: (l.productId ?? l.menuItemId ?? null) as number | string | null,
      });
    }
    const linesFromDescription = (row: Record<string, unknown>): BillLine[] => {
      try {
        const env = JSON.parse(String(row.description ?? '{}')) as { items?: Array<Record<string, unknown>>; lines?: Array<Record<string, unknown>> };
        const src = (Array.isArray(env.items) && env.items.length ? env.items : (Array.isArray(env.lines) ? env.lines : []));
        return src.map((it) => ({
          name: String(it.name ?? it.itemName ?? '—'),
          qty: Number(it.quantity ?? it.qty ?? 1) || 1,
          rate: Number(it.unitPrice ?? it.rate ?? it.price ?? 0) || 0,
          taxRate: Number(it.taxRate ?? 0) || 0,
          productId: (it.productId ?? it.menuItemId ?? null) as number | string | null,
        }));
      } catch { return []; }
    };
    const descCustomer = (row: Record<string, unknown>): string => {
      try { const d = String(row.description ?? ''); return d.startsWith('{') ? String((JSON.parse(d) as { customerName?: string }).customerName ?? '') : ''; }
      catch { return ''; }
    };
    const result: OpenOrder[] = [];
    for (const row of orders) {
      const id = row.id as number | string | undefined;
      if (id == null) continue;
      if (String(row.code ?? '').startsWith('SAMPLE-')) continue;
      const status = String((row as { orderStatus?: unknown }).orderStatus ?? row.status ?? '').toLowerCase();
      if (DONE.has(status)) continue;
      // Already billed → skip.
      const inv = (row as { salesInvoiceId?: number | string | null }).salesInvoiceId;
      if (inv != null && String(inv) !== '0') continue;

      const lines = (byOrder.get(String(id)) ?? linesFromDescription(row)).filter((l) => l.qty > 0);
      if (!lines.length) continue; // nothing to bill

      const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
      const tax = lines.reduce((s, l) => s + l.qty * l.rate * (l.taxRate / 100), 0);
      const tableId = row.tableId != null && String(row.tableId) !== '0' ? String(row.tableId) : '';
      result.push({
        id,
        code: String(row.code ?? `#${id}`),
        number: String((row as { orderNumber?: unknown }).orderNumber ?? row.code ?? `#${id}`),
        customerName: String(row.customerName ?? '') || descCustomer(row) || 'Walk-in',
        tableLabel: (tableId && tableMap.get(tableId)) || (tableId ? `Table ${tableId}` : 'Takeaway'),
        lines,
        subTotal: round2(subTotal),
        tax: round2(tax),
        grand: round2(subTotal + tax),
      });
    }
    return result;
  }, [orders, orderLines, tableMap]);

  async function generateBill(o: OpenOrder) {
    setBilling(String(o.id));
    try {
      const now = new Date();
      const invCode = `DIN-${now.toISOString().slice(2, 10).replace(/-/g, '')}-${now.toISOString().slice(11, 19).replace(/:/g, '')}`;
      const invDescription = JSON.stringify({
        source: 'Dine-in', restaurantOrderId: o.id, customerName: o.customerName,
        lines: o.lines.map((l) => ({ name: l.name, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate, lineTotal: round2(l.qty * l.rate * (1 + l.taxRate / 100)) })),
        subTotal: o.subTotal, taxAmount: o.tax, grandTotal: o.grand, issuedAt: now.toISOString(),
      });
      // Reliable create: returns the real id even though POST echoes no body.
      const { id: invId } = await createHeaderAndGetId('/sales/SalesInvoice', {
        code: invCode, name: `Dine-in ${o.tableLabel}`, invoiceNumber: invCode,
        invoiceDate: now.toISOString(), customerName: o.customerName, currency: 'INR',
        invoiceType: 'Dine-in', invoiceStatus: 'Posted', paymentStatus: 'Unpaid',
        description: invDescription, subTotal: o.subTotal, taxAmount: o.tax,
        grandTotal: o.grand, paidAmount: 0, balance: o.grand, status: 0,
      }, { matchField: 'invoiceNumber', matchValue: invCode });

      if (invId == null) { toast.error('Bill failed', 'Could not create the invoice.'); return; }

      // Line rows keyed off the invoice id (globally unique code → no 409).
      const saved = await postLinesSequential('/sales/SalesInvoiceLine', o.lines.map((l, i) => ({
        code: `${invId}-L${i + 1}`, name: l.name || `Line ${i + 1}`, status: 0,
        salesInvoiceId: invId, lineNumber: i + 1, productId: l.productId ?? null, productName: l.name,
        quantity: l.qty, unitOfMeasure: 'PCS', unitPrice: l.rate,
        taxableAmount: round2(l.qty * l.rate), taxRate: l.taxRate,
        taxAmount: round2(l.qty * l.rate * (l.taxRate / 100)),
        lineTotal: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
      })));

      // Stock OUT (best-effort — never block the bill).
      try {
        const warehouseId = await resolveDefaultWarehouseId();
        await postStockMovements({
          direction: 'Out', sourceType: 'Restaurant', sourceId: invId, docNumber: invCode, warehouseId,
          lines: o.lines.map((l) => ({ productId: l.productId ?? null, quantity: l.qty, unitCost: l.rate, unitOfMeasure: 'PCS' })),
        });
      } catch { /* ignore */ }

      // Flip the order to Billed — spread the full row so the PUT (full replace)
      // doesn't null customerName/tableId/grandTotal/description.
      try {
        const raw = orders.find((r) => String(r.id) === String(o.id)) ?? {};
        await apiClient.put('/restaurant/RestaurantOrder', { ...raw, id: Number(o.id), status: 0, orderStatus: 'Billed', salesInvoiceId: invId });
      } catch { /* best-effort */ }

      toast.success('Bill generated', `Invoice ${invCode} created${saved < o.lines.length ? ` (${saved}/${o.lines.length} lines)` : ''}`);
      await load();
      onBilled?.();
    } catch (e) {
      toast.error('Bill failed', e instanceof Error ? e.message : 'Network error');
    } finally {
      setBilling(null);
    }
  }

  if (!loading && open.length === 0) return null; // nothing open → don't clutter the page

  return (
    <Card className="mb-4 p-3">
      <div className="mb-2 flex items-center gap-2">
        <UtensilsCrossed size={15} className="text-orange-500" />
        <span className="text-sm font-semibold text-content-primary">Open Orders — ready to bill</span>
        <span className="text-[11px] text-content-muted">{open.length}</span>
        <button type="button" onClick={() => void load()} className="ml-auto inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-primary">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {open.map((o) => (
          <div key={String(o.id)} className="flex flex-col rounded-md border border-border bg-surface-card p-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-content-primary">{o.tableLabel}</div>
                <div className="truncate text-[11px] text-content-muted">{o.customerName} · {o.lines.length} item(s)</div>
              </div>
              <div className="text-right text-[13px] font-semibold text-primary whitespace-nowrap">₹ {o.grand.toLocaleString('en-IN')}</div>
            </div>
            <Button size="sm" className="mt-2" disabled={billing === String(o.id)} onClick={() => void generateBill(o)}>
              <ReceiptText className="h-3.5 w-3.5" /> {billing === String(o.id) ? 'Billing…' : 'Generate Bill'}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
